import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import SelectBox from "../../../components/atoms/select";
import {
  useRoundScoreOrderList,
  useScoreCalendarList,
  useScoreOrderList,
} from "../../../@apis/hooks/useTournament";
import { useGetRoundList } from "../../../@apis/hooks/useScore";
import type { TournamentType } from "../../../@apis/dto/response/main.response";
import { cn } from "../../../lib/utils/cn";
import RankingNotice from "../../../components/atoms/RankingNotice";
import {
  formatPeoria,
  hasRankingScore,
  rankingDisplay,
} from "../../../lib/utils/tournamentUtils";
import {
  ScoreCardViewPanel,
  UserScoreCardsPanel,
} from "../../../components/layout/score/TournamentScoreCardPanels";

interface Props {
  tournamentId?: number | null;
  tournamentType?: TournamentType | null;
  tournamentName?: string;
  statusLabel?: string;
}

// MULTI_ROUND 전용 전체 선택 옵션 값
const ALL_VALUE = "all";

// 정렬 우선순위: 참가 → 컷오프 → 실격 → 기권 → 불참
const STATUS_PRIORITY: Record<string, number> = {
  PARTICIPATED: 0,
  CUTOFF: 1,
  DISQUALIFIED: 2,
  WITHDRAWN: 3,
  ABSENT: 4,
};

interface RankingRow {
  id: string;
  status: string;
  rank: string;
  name: string;
  totalScore: number | null; // 합산 타수
  combinedScore: number | null; // 합산 스코어(오버/언더)
  currentRoundScore: number | null; // 금회 스코어(오버/언더)
  progressDisplay: string;
  order: number;
  isMe: boolean;
  scoreCardId: number | null; // 스코어 카드 단건조회용
  calendarId: number | null; // 스코어 카드 단건/묶음 조회용
  userId: number | null; // 멀티라운드 스코어카드 묶음 조회용
  excludeRoundId: number | null; // 비참가 처리된 라운드 → 카드 제외용
  peoriaScore?: number;
  peoriaHandicap?: number;
}

// 오버/언더 표기: null → "-", 0 → E, 양수 → +n, 음수 → n
const formatScore = (v: number | null) =>
  v == null ? "-" : v === 0 ? "E" : v > 0 ? `+${v}` : String(v);

// 오버/언더 색상: 언더(-) 빨강 / 오버(+) 파랑 / 이븐 검정 / 없음 회색
const scoreColor = (v: number | null) =>
  v == null
    ? "text-[#8e8e93]"
    : v < 0
      ? "text-[#ff3b30]"
      : v > 0
        ? "text-[#007aff]"
        : "text-black";

// 비참가 상태 뱃지(순위 칸)
const statusBadge = (status: string) => {
  switch (status) {
    case "CUTOFF":
      return { label: "컷오프", cls: "bg-[#c7c7cc] text-white" };
    case "DISQUALIFIED":
      return { label: "실격", cls: "bg-[#f2f2f7] text-[#8e8e93]" };
    case "WITHDRAWN":
      return { label: "기권", cls: "bg-[#f2f2f7] text-[#8e8e93]" };
    case "ABSENT":
      return { label: "불참", cls: "bg-[#f2f2f7] text-[#8e8e93]" };
    default:
      return null;
  }
};

export default function CompetitionRankingBoard({
  tournamentId,
  tournamentType,
  tournamentName,
  statusLabel,
}: Props) {
  const isLegacy = tournamentType === "LEGACY";
  const isMultiRound = tournamentType === "MULTI_ROUND";

  // LEGACY: 스코어 일정 목록 / MULTI_ROUND: 대회 라운드 목록
  const { data: scoreCalendarData } = useQuery({
    ...useScoreCalendarList(isLegacy ? (tournamentId ?? 0) : 0),
  });
  const { data: roundListData } = useQuery({
    ...useGetRoundList(isMultiRound ? tournamentId : null),
  });

  const options = React.useMemo(() => {
    if (isLegacy) {
      return (scoreCalendarData?.data?.calendarList ?? []).map((item) => ({
        label: `${item.date} ${item.timeType}`,
        value: String(item.id),
      }));
    }
    if (isMultiRound) {
      const roundOptions = (roundListData?.data?.roundList ?? []).map(
        (item) => ({ label: item.roundName, value: String(item.id) }),
      );
      return [{ label: "전체 라운드", value: ALL_VALUE }, ...roundOptions];
    }
    return [];
  }, [isLegacy, isMultiRound, scoreCalendarData, roundListData]);

  // 비참가 라운드 태그명/누적 노출 판정용 맵 (id → roundName / roundOrder)
  const roundNameById = React.useMemo(() => {
    const map = new Map<number, string>();
    (roundListData?.data?.roundList ?? []).forEach((r) =>
      map.set(r.id, r.roundName),
    );
    return map;
  }, [roundListData]);
  const roundOrderById = React.useMemo(() => {
    const map = new Map<number, number>();
    (roundListData?.data?.roundList ?? []).forEach((r) =>
      map.set(r.id, r.roundOrder),
    );
    return map;
  }, [roundListData]);

  const [selected, setSelected] = React.useState("");
  const [gender, setGender] = React.useState(""); // 전체="" / 남자="1" / 여자="0"
  const [expandedId, setExpandedId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (options.length === 0) return;
    if (options.some((o) => o.value === selected)) return;
    setSelected(options[0].value);
  }, [options, selected]);

  // LEGACY: 일정(calendarId) 기준 순위
  const { data: scoreOrderData } = useQuery({
    ...useScoreOrderList(
      isLegacy ? (tournamentId ?? 0) : 0,
      isLegacy ? selected : null,
    ),
  });

  // 선택 전·전체는 roundId 없이 조회
  const roundIdParam =
    isMultiRound && selected && selected !== ALL_VALUE ? selected : null;
  // MULTI_ROUND: 라운드(roundId)/성별 기준 순위
  const { data: roundScoreOrderData } = useQuery({
    ...useRoundScoreOrderList(
      isMultiRound ? (tournamentId ?? 0) : 0,
      roundIdParam,
      gender === "" ? null : (Number(gender) as 0 | 1),
    ),
  });

  const orderData = isLegacy
    ? scoreOrderData?.data
    : isMultiRound
      ? roundScoreOrderData?.data
      : undefined;
  const { showPeoria, notice } = rankingDisplay(
    orderData,
    isMultiRound && roundIdParam != null,
  );

  const rows: RankingRow[] = React.useMemo(() => {
    const scoreList = orderData?.scoreList ?? [];
    const scoreRows: RankingRow[] = scoreList.map((item, i) => ({
      id: `s-${item.id}`,
      status: item.status,
      rank: item.rank,
      name: item.userName,
      totalScore: item.totalScore,
      combinedScore: item.combinedScore,
      currentRoundScore: item.currentRoundScore,
      progressDisplay: item.progressDisplay,
      order: i,
      isMe: item.isMe,
      scoreCardId: item.id,
      calendarId: item.calendarId,
      userId: item.userId,
      excludeRoundId: null,
      peoriaScore: item.peoriaScore,
      peoriaHandicap: item.peoriaHandicap,
    }));

    // 비참가자에는 calendarId가 없어 참가자 항목에서 가져옴 (멀티라운드 묶음 조회용)
    const sharedCalendarId = scoreList[0]?.calendarId ?? null;

    const nonParticipants = orderData?.nonParticipants ?? [];
    const nonRows: RankingRow[] = nonParticipants.map((item) => ({
      id: `n-${item.applyId}`,
      status: item.status,
      rank: "-",
      name: item.userName,
      totalScore: null,
      combinedScore: null,
      currentRoundScore: null,
      progressDisplay: "-",
      order: 0,
      isMe: item.isMe,
      scoreCardId: null,
      calendarId: isMultiRound ? sharedCalendarId : null,
      userId: isMultiRound ? item.userId : null,
      excludeRoundId: isMultiRound ? item.roundId : null,
    }));

    return [...scoreRows, ...nonRows].sort((a, b) => {
      const pa = STATUS_PRIORITY[a.status] ?? 99;
      const pb = STATUS_PRIORITY[b.status] ?? 99;
      if (pa !== pb) return pa - pb;
      if (a.status === "PARTICIPATED" || a.status === "CUTOFF") {
        return a.order - b.order;
      }
      return a.name.localeCompare(b.name, "ko");
    });
  }, [isMultiRound, orderData]);

  const myRow = React.useMemo(() => rows.find((r) => r.isMe), [rows]);
  const selectedLabel = options.find((o) => o.value === selected)?.label ?? "";

  // 합산 스코어 옆에 금회 스코어 컬럼(라운드 대회) 노출 여부
  const showCurrent = isMultiRound;

  return (
    <div className="flex flex-col">
      {/* 대회 정보 헤더 */}
      <div className="border-b-4 border-[#f2f2f7] px-[16px] py-[20px] flex flex-col gap-[16px]">
        {statusLabel && (
          <span className="w-fit bg-[#e5e5ea] text-[#8e8e93] text-[22px] font-medium px-[8px] py-[2px] rounded-[5px]">
            {statusLabel}
          </span>
        )}
        <div className="flex gap-[8px] items-start">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            className="shrink-0"
          >
            <path
              d="M14 1.4585C14.2321 1.4585 14.4546 1.55068 14.6187 1.71478C14.7828 1.87887 14.875 2.10143 14.875 2.3335V3.54216L20.7177 6.4635L20.7807 6.495C21.637 6.92316 22.3673 7.28833 22.876 7.643C23.3917 8.0035 23.933 8.52616 23.933 9.3335C23.933 10.1408 23.3917 10.6635 22.876 11.024C22.3673 11.3787 21.637 11.7438 20.7807 12.172L14.875 15.1237V21.0002C14.875 21.2322 14.7828 21.4548 14.6187 21.6189C14.4546 21.783 14.2321 21.8752 14 21.8752C13.7679 21.8752 13.5454 21.783 13.3813 21.6189C13.2172 21.4548 13.125 21.2322 13.125 21.0002V2.3335C13.125 2.10143 13.2172 1.87887 13.3813 1.71478C13.5454 1.55068 13.7679 1.4585 14 1.4585Z"
              fill="black"
            />
            <path
              d="M14.0026 25.6668C20.4461 25.6668 25.6693 23.8387 25.6693 21.5835C25.6693 19.6445 21.8076 18.0205 16.6276 17.604V21.0002C16.6276 21.6964 16.351 22.364 15.8588 22.8563C15.3665 23.3486 14.6988 23.6252 14.0026 23.6252C13.3064 23.6252 12.6387 23.3486 12.1464 22.8563C11.6542 22.364 11.3776 21.6964 11.3776 21.0002V17.604C6.1976 18.0205 2.33594 19.6445 2.33594 21.5835C2.33594 23.8387 7.5591 25.6668 14.0026 25.6668Z"
              fill="black"
            />
            <path
              d="M14 1.4585C13.7679 1.4585 13.5454 1.55068 13.3813 1.71478C13.2172 1.87887 13.125 2.10143 13.125 2.3335V21.0002C13.125 21.2322 13.2172 21.4548 13.3813 21.6189C13.5454 21.783 13.7679 21.8752 14 21.8752C14.2321 21.8752 14.4546 21.783 14.6187 21.6189C14.7828 21.4548 14.875 21.2322 14.875 21.0002V15.1248L20.7807 12.172C21.637 11.7438 22.3673 11.3787 22.876 11.024C23.3917 10.6635 23.933 10.142 23.933 9.3335C23.933 8.52616 23.3917 8.0035 22.876 7.643C22.3673 7.28833 21.637 6.92316 20.7807 6.495L14.875 3.54333V2.3335C14.875 2.10143 14.7828 1.87887 14.6187 1.71478C14.4546 1.55068 14.2321 1.4585 14 1.4585Z"
              fill="black"
            />
          </svg>
          <p className="flex-1 text-[22px] font-medium text-black break-keep">
            {tournamentName ?? ""}
          </p>
        </div>

        {/* 나의 순위 */}
        {myRow && (
          <div className="bg-[#fff8cc] rounded-[10px] p-[12px] flex flex-col gap-[12px]">
            {selectedLabel && (
              <p className="text-base font-semibold text-black">
                {selectedLabel}
              </p>
            )}
            <div className="flex flex-col gap-[4px]">
              <div className="flex items-center justify-between text-black">
                <p className="text-base font-semibold">나의 순위</p>
                {myRow.status === "PARTICIPATED" ? (
                  <p className="font-medium">
                    <span className="text-2xl">{myRow.rank}</span>
                    {myRow.rank !== "-" && (
                      <span className="text-[22px]">위</span>
                    )}
                  </p>
                ) : (
                  (() => {
                    const b = statusBadge(myRow.status);
                    return b ? (
                      <span
                        className={`px-[10px] h-[21px] grid place-items-center rounded-[5px] text-xs font-medium ${b.cls}`}
                      >
                        {b.label}
                      </span>
                    ) : null;
                  })()
                )}
              </div>
              <div className="flex items-start justify-between font-semibold">
                <p className="text-base text-black">{myRow.name}</p>
                <div className="flex flex-col items-end">
                  <div className="flex items-center gap-[4px]">
                    <span className="text-sm text-[#8e8e93]">합산</span>
                    <span className="w-[60px] flex items-center justify-end gap-[4px]">
                      <span className="text-base text-black">
                        {myRow.totalScore ?? "-"}
                      </span>
                      <span
                        className={`text-sm ${scoreColor(myRow.combinedScore)}`}
                      >
                        ({formatScore(myRow.combinedScore)})
                      </span>
                    </span>
                  </div>
                  {showCurrent && (
                    <div className="flex items-center gap-[4px]">
                      <span className="text-sm text-[#8e8e93]">금회</span>
                      <span className="w-[60px] flex items-center justify-end">
                        <span
                          className={`text-base ${scoreColor(myRow.currentRoundScore)}`}
                        >
                          {formatScore(myRow.currentRoundScore)}
                        </span>
                      </span>
                    </div>
                  )}
                  {showPeoria && hasRankingScore(myRow.status) && (
                    <>
                      <div className="flex items-center gap-[4px]">
                        <span className="text-sm text-[#8e8e93]">최종</span>
                        <span className="w-[60px] flex items-center justify-end">
                          <span className="text-base text-black">
                            {formatPeoria(myRow.peoriaScore)}
                          </span>
                        </span>
                      </div>
                      {myRow.peoriaHandicap != null && (
                        <span className="text-xs text-[#8e8e93]">
                          핸디캡 {formatPeoria(myRow.peoriaHandicap)}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 필터 + 순위 표 */}
      <div className="px-[16px] py-[20px] flex flex-col gap-[12px]">
        <SelectBox
          options={options}
          value={selected}
          onChange={(e) => {
            setSelected(e.target.value);
            setExpandedId(null);
          }}
          placeholder="선택"
          className="text-base h-auto py-[12px] px-[16px]"
        />

        {/* 성별 필터(라운드 대회 전용) */}
        {isMultiRound && (
          <div className="flex gap-[8px]">
            {[
              { label: "전체", value: "" },
              { label: "남자", value: "1" },
              { label: "여자", value: "0" },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => {
                  setGender(opt.value);
                  setExpandedId(null);
                }}
                className={`px-[20px] py-[8px] rounded-full text-sm font-medium ${
                  gender === opt.value
                    ? "bg-black text-white"
                    : "bg-white border border-[#8e8e93] text-[#8e8e93]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {rows.length > 0 && <RankingNotice lines={notice} />}

        {/* 순위 표: 컬럼 폭을 내용에 맞춰 자동 조정(순위/스코어/진행홀은 auto, 이름은 남는 공간) */}
        <div
          className={cn(
            "grid text-sm",
            showCurrent
              ? "grid-cols-[auto_minmax(0,1fr)_auto_auto_auto]"
              : "grid-cols-[auto_minmax(0,1fr)_auto_auto]",
          )}
        >
          {/* 헤더 */}
          <div className="bg-[#f2f2f7] rounded-tl-[10px] p-[8px] flex items-center justify-center font-semibold text-black whitespace-nowrap">
            순위
          </div>
          <div className="bg-[#f2f2f7] p-[8px] flex items-center font-semibold text-black">
            이름
          </div>
          <div className="bg-[#f2f2f7] px-[12px] py-[8px] flex items-center justify-center text-center font-semibold text-black leading-[20px] whitespace-nowrap">
            {showCurrent ? (
              <>
                합산
                <br />
                스코어
              </>
            ) : (
              "합산 스코어"
            )}
          </div>
          {showCurrent && (
            <div className="bg-[#f2f2f7] px-[12px] py-[8px] flex items-center justify-center text-center font-semibold text-black leading-[20px]">
              금회
              <br />
              스코어
            </div>
          )}
          <div className="bg-[#f2f2f7] rounded-tr-[10px] px-[12px] py-[8px] flex items-center justify-center font-semibold text-black whitespace-nowrap">
            진행 홀
          </div>

          {/* 본문 */}
          {rows.length === 0 ? (
            <div className="col-span-full border-b border-l border-r border-[#e5e5ea] rounded-b-[10px] py-[60px] text-center text-sm text-[#8e8e93]">
              아직 대회가 시작되지 않았습니다.
            </div>
          ) : (
            rows.map((row, idx) => {
              const badge = statusBadge(row.status);
              const isCutoff = row.status === "CUTOFF";
              const isDisqGroup =
                row.status === "DISQUALIFIED" ||
                row.status === "WITHDRAWN" ||
                row.status === "ABSENT";
              const showScore = hasRankingScore(row.status);
              const isLast = idx === rows.length - 1;

              // 펼침 가능: 일정대회는 모든 행(비참가자는 빈 카드), 멀티라운드는 사용자 보유 행
              const canExpand = isLegacy
                ? true
                : row.userId != null && row.calendarId != null;
              const isExpanded = canExpand && expandedId === row.id;

              const rowBg = isExpanded
                ? "bg-[#EAF6FF]"
                : row.isMe
                  ? "bg-[#fff8cc]"
                  : isCutoff
                    ? "bg-[#f2f2f7]"
                    : isDisqGroup
                      ? "bg-[#d1d1d6]"
                      : "bg-white";
              // 마지막 행이면서 펼쳐지지 않았을 때만 셀에 하단 라운드 적용
              const roundBottom = isLast && !isExpanded;
              const onToggle = () =>
                canExpand &&
                setExpandedId((prev) => (prev === row.id ? null : row.id));

              // 모든 셀 공통(행 배경/하단 보더/정렬/클릭)
              const cell = cn(
                "p-[8px] flex items-center border-b border-[#e5e5ea] font-semibold",
                rowBg,
                canExpand && "cursor-pointer",
              );

              return (
                <React.Fragment key={row.id}>
                  {/* 순위 */}
                  <div
                    onClick={onToggle}
                    className={cn(
                      cell,
                      "border-l justify-center whitespace-nowrap",
                      roundBottom && "rounded-bl-[10px]",
                    )}
                  >
                    {badge ? (
                      <span
                        className={`px-[10px] h-[21px] grid place-items-center rounded-[5px] text-xs font-medium ${badge.cls}`}
                      >
                        {badge.label}
                      </span>
                    ) : (
                      <span className="text-black">{row.rank}</span>
                    )}
                  </div>
                  {/* 이름 */}
                  <div onClick={onToggle} className={cn(cell, "min-w-0")}>
                    <p className="truncate text-black">
                      {row.name}
                      {row.isMe && <span> (나)</span>}
                    </p>
                  </div>
                  {/* 합산 스코어 */}
                  <div
                    onClick={onToggle}
                    className={cn(
                      cell,
                      "px-[12px] justify-center whitespace-nowrap",
                      showPeoria && "flex-col",
                      showScore
                        ? scoreColor(row.combinedScore)
                        : "text-[#8e8e93]",
                    )}
                  >
                    {showScore ? formatScore(row.combinedScore) : "-"}
                    {showPeoria && showScore && (
                      <span className="text-xs font-medium text-black">
                        최종 {formatPeoria(row.peoriaScore)}
                      </span>
                    )}
                  </div>
                  {/* 금회 스코어 */}
                  {showCurrent && (
                    <div
                      onClick={onToggle}
                      className={cn(
                        cell,
                        "px-[12px] justify-center whitespace-nowrap",
                        showScore
                          ? scoreColor(row.currentRoundScore)
                          : "text-[#8e8e93]",
                      )}
                    >
                      {showScore ? formatScore(row.currentRoundScore) : "-"}
                    </div>
                  )}
                  {/* 진행 홀 */}
                  <div
                    onClick={onToggle}
                    className={cn(
                      cell,
                      "border-r px-[12px] justify-center whitespace-nowrap",
                      roundBottom && "rounded-br-[10px]",
                      row.progressDisplay.includes("H")
                        ? "text-[#8e8e93]"
                        : "text-black",
                    )}
                  >
                    {row.progressDisplay}
                  </div>

                  {/* 펼침: 스코어 카드 */}
                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        key="panel"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className={cn(
                          "col-span-full overflow-hidden border-b border-l border-r border-[#e5e5ea]",
                          isLast && "rounded-b-[10px]",
                        )}
                      >
                        {isMultiRound ? (
                          <UserScoreCardsPanel
                            tournamentId={tournamentId}
                            calendarId={row.calendarId}
                            userId={row.userId}
                            nonPartRoundId={row.excludeRoundId}
                            nonPartRoundName={
                              row.excludeRoundId != null
                                ? roundNameById.get(row.excludeRoundId)
                                : undefined
                            }
                            roundOrderById={roundOrderById}
                            maxRoundOrder={
                              selected === ALL_VALUE
                                ? null
                                : (roundOrderById.get(Number(selected)) ?? null)
                            }
                          />
                        ) : (
                          <ScoreCardViewPanel
                            tournamentId={tournamentId}
                            calendarId={row.calendarId}
                            scoreCardId={row.scoreCardId}
                          />
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </React.Fragment>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
